// Blip, the online IDE's coding helper (docs/BLIP_HELPER_DESIGN.md): his
// panel, where the user asks him for something and sees what he did, with
// him in its corner and his speech bubble above him. He appears only when
// the IDE's server offers him (web/server/lib/blip.mjs, GET /api/config),
// and only with his panel: turning him on opens it, closing it turns him off.
// The user can show him pictures with their message, and sketch on the
// world seen from above (guides.js, docs/BLIP_GUIDE_PICTURES_DESIGN.md).
//
// A request goes to agent.js, which runs the conversation, carrying out his
// tools (tools.js) in the page. Each request's changes end in a card that
// can undo them all, and the editor marks the lines he changed. Each model
// or picture he has made (models.js) has a card of its own, which follows
// its job until it is saved in the project, even after the page reloads.

import { PROJECT_FILE } from "../filetree.js";
import { BlipError, converse, pictureRoom, tooLarge } from "./agent.js";
import { BlipFigure } from "./character.js";
import { Chats, chatTokens, newChat } from "./chats.js";
import { ideContext } from "./context.js";
import { CANNOT_OPEN, GuideError, MESSAGE_PICTURES, SketchPad, chatPicture, imageBlock, pictureLines, prepareGuide,
  shownPictures } from "./guides.js";
import { makerOf } from "./models.js";
import { Listener, speechAvailable } from "./speech.js";
import { TODO_CONTEXT, isTodo } from "./todo.js";
import { BlipTools, changedRows } from "./tools.js";

// The knowledge pack the site was built with (build.mjs).
const PACK = typeof B3D_BLIP_PACK === "string" ? B3D_BLIP_PACK : "";
const SHOWN = "b3d-ide:blip", HELLO = "b3d-ide:blip-hello", LEVEL = "b3d-ide:blip-level", PLACE = "b3d-ide:blip-place";

// How hard Blip tries, which the user picks under his conversation: the
// server lists the levels, lowest first, each a model (lib/blip.mjs,
// BLIP_DEFAULTS.models). These are for a server that does not, and say what
// the known levels are for.
const LEVELS = [
  { level: "min", model: "claude-haiku-5-5" },
  { level: "medium", model: "claude-sonnet-5-5" },
  { level: "max", model: "claude-opus-5-5" },
  { level: "ultra", model: "claude-opus-5-5" },
];
const ABOUT = {
  min: "quick answers to small questions, using the least energy",
  medium: "good for most jobs",
  max: "for hard problems and big changes, using more energy",
  ultra: "as max, with the most detailed pictures and textures, using the most energy",
};
// The levels' names before 23 October 2026, as a browser may remember them.
const OLD_LEVELS = { low: "min", high: "max" };
// He is his usual size at medium, and a fifth bigger or smaller for each
// level above or below it.
const levelScale = (index, normal) => Math.max(0.6, Math.min(1.6, 1 + 0.2 * (index - normal)));
// claude-haiku-5-5 -> Claude Haiku 5.5
const modelName = (id) => id.replace(/^claude-/, "").split("-").reduce((name, part) =>
  /^[0-9]+$/.test(part) ? `${name}${/[0-9]$/.test(name) ? "." : " "}${part}` : `${name} ${part[0].toUpperCase()}${part.slice(1)}`,
"Claude");
const LONG_CHAT = 100000;
// The most steps of a TODO note's request (todo.js): a few looks, and the note.
const TODO_STEPS = 8;
const PLACEHOLDER = "Ask Blip to make, change, fix or explain something. You can paste or drop a picture.";
const FULL = "This chat is full of pictures. Start a new chat to show Blip more.";
const THREE = "Blip takes three pictures a message: send these, then show him more.";
// The picture button's mountain and sun.
const PICTURE_ICON = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><rect x="1.5" y="2.5" width="13" ' +
  'height="11" rx="1.5" fill="none" stroke="currentColor"/><circle cx="11" cy="6" r="1.5" fill="currentColor"/>' +
  '<path d="M2 12.5 6 7.5l3 3.5 2-2 3 3.5z" fill="currentColor"/></svg>';

// An element: h("div", { class: "x", onclick }, child, ...).
function h(tag, attributes = {}, ...children) {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith("on")) element.addEventListener(key.slice(2), value);
    else if (key === "class") element.className = value;
    else if (key === "text") element.textContent = value;
    else element.setAttribute(key, value === true ? "" : value);
  }
  for (const child of children.flat()) if (child !== null && child !== undefined) element.append(child);
  return element;
}

const escape = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Blip's replies, in a little Markdown: paragraphs, lists, code blocks,
// `code` and **bold**. Everything is escaped first.
const inline = (line) => escape(line).replace(/`([^`]+)`/g, "<code>$1</code>")
  .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
const ITEM = /^(\s*)([-*•]|\d+[.)])\s+/;
const FENCE = /^\s*```/;
const HEADING = /^#{1,6}\s/;

// A list's items, { indent, ordered, lines }, nested by their indentation.
function list(items) {
  let html = "";
  const open = [];
  for (const item of items) {
    while (open.length && item.indent < open[open.length - 1].indent) html += `</li></${open.pop().tag}>`;
    const top = open[open.length - 1];
    if (!top || item.indent > top.indent) {
      const tag = item.ordered ? "ol" : "ul";
      open.push({ indent: item.indent, tag });
      html += `<${tag}><li>`;
    } else {
      html += "</li><li>";
    }
    html += item.lines.map(inline).join("<br>");
  }
  while (open.length) html += `</li></${open.pop().tag}>`;
  return html;
}

export function markdown(text) {
  const parts = [];
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  for (let at = 0; at < lines.length;) {
    const line = lines[at];
    if (FENCE.test(line)) {
      const code = [];
      for (++at; at < lines.length && !FENCE.test(lines[at]); ++at) code.push(lines[at]);
      ++at;
      parts.push(`<pre><code>${escape(code.join("\n"))}</code></pre>`);
    } else if (!line.trim()) {
      ++at;
    } else if (HEADING.test(line)) {
      parts.push(`<p><strong>${inline(line.replace(/^#+\s*/, ""))}</strong></p>`);
      ++at;
    } else if (ITEM.test(line)) {
      // A list: its items, the lines that go on from them, and the items
      // indented under them.
      const items = [];
      for (; at < lines.length && lines[at].trim() && !FENCE.test(lines[at]); ++at) {
        const match = ITEM.exec(lines[at]);
        if (match) items.push({ indent: match[1].length, ordered: /\d/.test(match[2]), lines: [lines[at].slice(match[0].length)] });
        else if (/^\s/.test(lines[at])) items[items.length - 1].lines.push(lines[at].trim());
        else break;
      }
      parts.push(list(items));
    } else {
      const paragraph = [];
      for (; at < lines.length && lines[at].trim() && !ITEM.test(lines[at]) && !FENCE.test(lines[at]) &&
        !HEADING.test(lines[at]); ++at) paragraph.push(lines[at]);
      parts.push(`<p>${paragraph.map(inline).join("<br>")}</p>`);
    }
  }
  return parts.join("");
}

// The last line of a progress note, for the bubble.
const lastLine = (text) => {
  const lines = (text || "").split("\n").map((line) => line.trim()).filter(Boolean);
  const line = lines[lines.length - 1] || "";
  return line.length > 110 ? `${line.slice(0, 107)}…` : line;
};

// What Blip looks like while a tool runs.
const FIGURE = { compile: "thinking", run_program: "watching", lookup_command: "reading", search_help: "reading",
  read_help: "reading", world_state: "reading", world_entity: "reading", world_asset: "reading", world_edit: "writing",
  world_action: "writing", world_probe: "thinking", world_route: "thinking", world_look: "watching", open_world_editor: "writing",
  online_test: "writing", make_model: "watching", change_model: "watching", make_picture: "watching", offer_choices: "talking",
  find_assets: "reading", add_asset: "writing", save_todo: "writing", draw_icon: "writing" };
const MODEL_TOOLS = ["make_model", "change_model", "make_picture"];

// The assets a change of Blip's to the world touched ([{ id, kind }]), as a
// change card names them: "a material", "2 particle effects".
const ASSET_NOUNS = { material: ["a material", "materials"], particles: ["a particle effect", "particle effects"],
  environment: ["an environment", "environments"], postfx: ["camera effects", "camera effects"],
  "sound-definition": ["a sound", "sounds"], sound: ["a sound", "sounds"], soundscape: ["a soundscape", "soundscapes"],
  physics: ["physics", "physics"],
  model: ["a model", "models"], texture: ["a picture", "pictures"], "terrain-definition": ["a terrain", "terrains"] };
export function worldAssetNouns(assets) {
  const counts = new Map();
  for (const asset of assets) {
    const nouns = ASSET_NOUNS[asset && asset.kind] || ["an asset", "assets"];
    counts.set(nouns, (counts.get(nouns) || 0) + 1);
  }
  return [...counts].map(([[one, many], count]) => count === 1 || one === many ? one : `${count} ${many}`);
}

// The energy bar's tooltip with membership: the week's credits and bought ones.
function planTitle(plan) {
  if (!plan.plan) return plan.lapsed ? "Your Blitz3D+ membership has ended." : "Blip comes with Blitz3D+ membership.";
  const day = new Date(plan.week.renews).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  return `${plan.week.left} of your ${plan.week.credits} weekly credits left. Blip refills ${day}.` +
    (plan.bought.credits > 0 ? ` Then ${plan.bought.credits} bought credits.` : "");
}

// A share of a day's energy, as the panel says it.
const percent = (share) => `${Math.max(share > 0 ? 1 : 0, Math.round((share || 0) * 100))}%`;
const figureFor = (call) => call.name === "str_replace_based_edit_tool" ?
  ((call.input && call.input.command) === "view" ? "reading" : "writing") : FIGURE[call.name] || "thinking";

// ide: main.js's hooks: { $, cloud, remember, editor, status, project(),
// mode(), isText(path), projectPath(file), refreshFiles(), compile(),
// checkRun(options), markErrors(errors, show), help, commands, examples(),
// lastRun(), running(), openFile(path), removeFile(path), openAccount(),
// closeMenu() }. Returns Blip's controls, or null when the server does not
// offer him.
export async function setupBlip(ide) {
  const { $, cloud, remember } = ide;
  const pack = ide.packVersion || PACK;
  const config = cloud && cloud.config && cloud.config.blip;
  // View > Blip helper and the toolbar's Blip turn him on and off.
  const menuItem = $("menu-blip");
  const toolButton = $("tool-blip");
  if (!config || !pack) {
    if (menuItem) menuItem.hidden = true;
    if (toolButton) toolButton.hidden = true;
    return null;
  }
  const chats = await Chats.open();
  const api = new URL("blip/messages", cloud.base).href;
  const tools = new BlipTools(ide);
  // The desktop IDE's Blip window (desktop.js) keeps the panel open, filling
  // the window, and follows a request through these hooks.
  const docked = !!ide.docked;
  const hooks = ide.blipHooks || {};

  // ------------------------------------------------------------ the parts
  const canvas = h("canvas", { class: "blip-canvas", "aria-hidden": "true" });
  // Blip himself, standing in his panel's bottom corner. A click on him goes
  // to the message box.
  const button = h("button", { type: "button", class: "blip-button", id: "blip-button", "aria-hidden": "true", tabindex: "-1" },
    canvas);
  const bubbleText = h("span", { class: "blip-bubble-text" });
  const bubbleButtons = h("span", { class: "blip-bubble-buttons" });
  const bubbleElement = h("div", { class: "blip-bubble", role: "status", hidden: true }, bubbleText, bubbleButtons);
  const energyBar = h("span", { class: "blip-energy-bar" });
  const energy = h("span", { class: "blip-energy", hidden: true }, energyBar);
  // With membership, the bar is the week's credits, and bought credits show
  // as a number beside it (docs/MEMBERSHIP_PLANS_DESIGN.md, "Bought credits").
  const bought = h("span", { class: "blip-bought", hidden: true });
  const log = h("div", { class: "blip-log", role: "log", "aria-live": "polite", "aria-label": "Your conversation with Blip" });
  const starters = h("div", { class: "blip-starters" });
  const working = h("p", { class: "blip-working", role: "status", hidden: true });
  const input = h("textarea", { class: "blip-input", rows: "2", "aria-label": "Your message to Blip", placeholder: PLACEHOLDER });
  const send = h("button", { type: "submit", class: "blip-send" }, "Send");
  // Pictures to show him (guides.js): the button, pasting and dropping, and
  // the sketch pad while the World Editor is open.
  const pictureChooser = h("input", { type: "file", accept: "image/*", multiple: true, hidden: true,
    onchange: () => {
      const files = [...pictureChooser.files];
      pictureChooser.value = "";
      addGuides(files);
    } });
  const pictureButton = h("button", { type: "button", class: "blip-picture-button", title: "Show Blip a picture",
    "aria-label": "Show Blip a picture", onclick: () => pictureChooser.click() });
  pictureButton.innerHTML = PICTURE_ICON;
  const sketchButton = h("button", { type: "button", class: "blip-sketch-button", hidden: true,
    title: "Draw on the world seen from above, to show Blip where things go", onclick: () => openSketch() }, "Sketch");
  const guideRow = h("div", { class: "blip-guides", hidden: true });
  const stopButton = h("button", { type: "button", class: "blip-stop", hidden: true }, "Stop");
  // Talking instead of typing, where the server can listen (speech.js).
  const talkButton = h("button", { type: "button", class: "blip-talk", hidden: !speechAvailable(cloud),
    title: "Talk to Blip: click, say what you want, then click again", "aria-pressed": "false" }, "Talk");
  const form = h("form", { class: "blip-form" }, input, pictureChooser,
    h("div", { class: "blip-form-buttons blip-form-pictures" }, sketchButton, pictureButton),
    h("div", { class: "blip-form-buttons" }, talkButton, send, stopButton));
  const levels = (Array.isArray(config.levels) && config.levels.length ? config.levels : LEVELS).map((entry) =>
    ({ ...entry, label: entry.level[0].toUpperCase() + entry.level.slice(1) }));
  const normal = Math.max(0, levels.findIndex((entry) => entry.level === "medium"));
  levels.forEach((entry, index) => { entry.scale = levelScale(index, normal); });
  const levelButtons = levels.map((entry) => {
    const picture = h("canvas", { class: "blip-effort-picture", "aria-hidden": "true" });
    const title = `${entry.label} effort: ${modelName(entry.model)}${ABOUT[entry.level] ? `, ${ABOUT[entry.level]}` : ""}`;
    return h("button", { type: "button", class: "blip-effort-option", role: "radio", "data-level": entry.level, title,
      "aria-label": title, onclick: () => setLevel(entry.level, true), onkeydown: (event) => levelKey(event) },
    picture, h("span", { text: entry.label }));
  });
  const effortRow = h("div", { class: "blip-effort" },
    h("span", { class: "blip-effort-label", id: "blip-effort-label", text: "Effort" }),
    h("div", { class: "blip-effort-options", role: "radiogroup", "aria-labelledby": "blip-effort-label" }, levelButtons));
  const signInNote = h("div", { class: "blip-signin", hidden: true },
    h("p", { text: "Sign in to talk to Blip. Your programs keep working without an account." }),
    h("button", { type: "button", class: "blip-primary", onclick: () => ide.openAccount() }, "Sign in"));
  // What is sent where, from the head's i button.
  const note = h("p", { class: "blip-note", id: "blip-note", title: "", hidden: true },
    "What you type, pictures you show, and the files Blip reads are sent to Anthropic's " +
    "Claude to answer." + (tools.models.available("model") ? " The descriptions and pictures he has models made from go to Tripo." :
    "") + (tools.models.available("picture") ? " Those he has pictures and textures made from go to OpenAI." : "") +
    (tools.models.available("sound") || tools.models.available("music") ? " Those he has sounds and music made from go to " +
      "ElevenLabs." : ""));
  const infoButton = h("button", { type: "button", class: "blip-info", title: "What Blip sends, and where",
    "aria-label": "What Blip sends, and where", "aria-expanded": "false", "aria-controls": "blip-note",
    onclick: () => showNote(note.hidden) }, "i");
  // Online, the head moves the panel (moving it, below).
  const head = h("header", { class: "blip-head", title: docked ? null : "Drag to move Blip's panel. Double-click to put it back" },
    h("h2", { id: "blip-title" }, "Blip"),
    energy,
    bought,
    h("button", { type: "button", class: "blip-new", title: "Start a new chat with Blip", onclick: () => newConversation() },
      "New chat"),
    infoButton,
    h("button", { type: "button", class: "blip-close", "aria-label": "Close Blip's panel", title: "Close (Esc)",
      onclick: () => close() }, "×"),
    note);
  const foot = h("div", { class: "blip-foot" }, form, button);
  // His bubble is just above him, under the rest of the panel.
  const panel = h("section", { class: "blip-panel", id: "blip-panel", role: "dialog", "aria-labelledby": "blip-title",
    hidden: true },
  head, log, starters, working, signInNote, effortRow, guideRow, bubbleElement, foot);
  // A picture, large (showPicture), and what a drag over the panel can drop.
  const viewerPicture = h("img", { class: "blip-viewer-picture", alt: "" });
  const viewerCaption = h("p", { class: "blip-viewer-caption" });
  const viewer = h("div", { class: "blip-viewer", role: "dialog", "aria-label": "A picture, large", tabindex: "-1", hidden: true,
    onclick: () => closeViewer() }, viewerPicture, viewerCaption);
  const dropHint = h("div", { class: "blip-drop", hidden: true }, h("p", { text: "Drop to show Blip" }));
  panel.append(viewer, dropHint);
  const root = h("div", { class: `blip${docked ? " blip-docked" : ""}`, id: "blip" }, panel);
  document.body.append(root);
  const figure = new BlipFigure(canvas);

  // ------------------------------------------------------------- the state
  let chat = null;
  let busy = null;                 // { controller } while a request runs
  // Removals waiting for the user's yes (confirmRemoval): item -> resolve.
  const removalAnswers = new WeakMap();
  let shown = remember.get(SHOWN) !== "off";
  let bubbleTimer = null;
  const offered = new Set();
  let usage = null;
  let guides = [];                 // the pictures to go with the next message (guides.js)
  let preparing = 0;               // and those still being prepared
  let sendWhenReady = false;       // Send was pressed while some were
  let guideNote = "";
  let todoShown = false;           // whether the row says what becomes of a TODO note
  let sketch = null;               // the open sketch pad
  let viewerUrl = null;
  let place = null;                // where the user put his panel online: { left, top }
  try {
    const kept = JSON.parse(remember.get(PLACE) || "null");
    if (kept && Number.isFinite(kept.left) && Number.isFinite(kept.top)) place = kept;
  } catch { /* where it opens first */ }
  const levelOf = (name) => levels.find((entry) => entry.level === name);
  const remembered = OLD_LEVELS[remember.get(LEVEL)] || remember.get(LEVEL);
  let level = levelOf(remembered) ? remembered : levelOf(config.level) ? config.level : levels[normal].level;
  setLevel(level, false);

  const signedIn = () => !!(cloud && cloud.user);
  const canAsk = () => signedIn() || config.signedOut;
  const save = () => chat ? chats.put(chat) : Promise.resolve();

  function bubble(text, buttons = [], seconds = 0) {
    clearTimeout(bubbleTimer);
    if (!text || !shown) {
      bubbleElement.hidden = true;
      return;
    }
    bubbleText.textContent = text;
    bubbleButtons.replaceChildren(...buttons.map(([label, action]) =>
      h("button", { type: "button", onclick: () => { bubble(null); action(); } }, label)));
    bubbleElement.hidden = false;
    if (seconds) bubbleTimer = setTimeout(() => bubble(null), seconds * 1000);
  }

  // What Blip is doing, for his figure in the World Editor's views
  // (editor_assistants.bb), with his size for the effort level.
  function presence(state, caption = "") {
    tools.world.presence({ ...(state ? { state } : {}), ...(caption ? { caption: caption.replace(/…/g, "...") } : {}),
      scale: (levelOf(level) || levels[normal]).scale });
  }

  // What Blip is doing: a line in his panel.
  function progress(text) {
    if (hooks.progress) hooks.progress(text || "");
    working.textContent = text || "";
    working.hidden = !text || !busy;
  }

  // Whether he is on, as View > Blip helper and the toolbar's Blip show it.
  // Online, he is on while his panel is open (open and close).
  function show(on) {
    shown = on;
    remember.set(SHOWN, on ? "on" : "off");
    root.hidden = !on;
    figure.show(on);
    if (menuItem) menuItem.setAttribute("aria-checked", String(on));
    if (toolButton) toolButton.setAttribute("aria-pressed", String(on));
    if (!on) bubble(null);
  }

  // ------------------------------------------------------------ his effort
  // Each option shows him at its size, the differences made larger to be
  // seen at this size; picking one makes him that size.
  function drawLevels() {
    const ratio = devicePixelRatio || 1;
    for (const [index, button] of levelButtons.entries()) {
      const canvas = button.querySelector("canvas");
      const box = canvas.getBoundingClientRect();
      if (!box.width) continue;
      canvas.width = Math.round(box.width * ratio);
      canvas.height = Math.round(box.height * ratio);
      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      window.BlipCharacter.draw(ctx, { x: box.width / 2, y: box.height - 3, size: 13 * (1 + 1.3 * (levels[index].scale - 1)), yaw: -16,
        pitch: 4, roll: 0, lift: 0, eyes: "normal", mouth: "smile", lookX: 0, lookY: 0.2 }, 0,
      { palette: figure.palette, reduceMotion: true });
    }
  }

  function setLevel(name, picked) {
    const entry = levelOf(name);
    if (!entry) return;
    const higher = entry.scale > figure.scaleTarget;
    level = name;
    if (picked) remember.set(LEVEL, name);
    for (const button of levelButtons) {
      const on = button.dataset.level === name;
      button.setAttribute("aria-checked", String(on));
      button.tabIndex = on ? 0 : -1;
    }
    figure.setScale(entry.scale);
    if (picked) {
      presence();
      if (hooks.level) hooks.level(entry.scale);
    }
    if (picked && !busy && figure.state !== "sleepy") figure.set(higher ? "pleased" : "resting");
  }

  // The arrow keys move between the options, as in any radio group.
  function levelKey(event) {
    const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const index = levels.findIndex((entry) => entry.level === level);
    const next = levels[(index + step + levels.length) % levels.length];
    setLevel(next.level, true);
    levelButtons[levels.indexOf(next)].focus();
  }

  // --------------------------------------------------------------- the log
  function itemElement(item) {
    switch (item.kind) {
      case "user":
        // The pictures shown with it, numbered across the chat, and a TODO
        // note's tag.
        return h("div", { class: "blip-message blip-user" },
          item.todo ? h("span", { class: "blip-todo-tag", title: "A TODO note: Blip writes it up for later", text: "TODO" }) : null,
          item.text ? h("p", { text: item.text }) : null,
          item.pictures && item.pictures.length ? h("div", { class: "blip-user-pictures" }, item.pictures.map((picture) =>
            h("button", { type: "button", class: "blip-thumb", title: `Picture ${picture.n}: show it large`,
              onclick: () => showPicture(picture.n) },
            h("img", { src: picture.thumb, alt: `Picture ${picture.n}${picture.sketch ? ", a sketch on the world" : ""}` }),
            h("span", { text: `Picture ${picture.n}` })))) : null);
      case "blip": {
        const element = h("div", { class: "blip-message blip-reply" });
        element.innerHTML = markdown(item.text || "…");
        return element;
      }
      case "steps": {
        const list = h("ol", {}, item.steps.map((step) => h("li", { text: step })));
        // Open or closed as the user left it, as it is drawn again.
        return h("details", { class: "blip-steps", open: !!item.open, ontoggle: (event) => { item.open = event.target.open; } },
          h("summary", { text: `What Blip did (${item.steps.length} ${item.steps.length === 1 ? "step" : "steps"})` }), list);
      }
      case "changes": {
        const names = item.changes.map((change) => change.before === null ? `added ${change.path}` : `changed ${change.path}`);
        // The main file he chose, the one Run starts, is part of his change.
        if (item.main) names.push(`made ${item.main.after} the file Run starts`);
        const text = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
        const actions = item.undone ? [h("span", { class: "blip-undone", text: item.undoneText || "Undone." })] : [
          h("button", { type: "button", onclick: () => showChanges(item) }, "Show changes"),
          h("button", { type: "button", onclick: () => undo(item) }, "Undo"),
        ];
        return h("div", { class: "blip-changes" }, h("p", { text: text[0].toUpperCase() + text.slice(1) + "." }),
          h("div", { class: "blip-change-buttons" }, actions));
      }
      case "world": {
        // His change to the world in the World Editor: Show selects and
        // frames it, and Undo takes it back alone (blip/world.js).
        // "Added 8 objects", "Added 1 object and changed 2", "Removed 7 objects",
        // and the assets, settings and ground he changed with them: "Changed
        // 1 object in the world, with a material", "Changed an environment
        // and the world's settings", "Changed the ground of Hills".
        const counts = [["added", item.made.length], ["changed", item.changed.length], ["removed", item.removed.length]]
          .filter(([, count]) => count).map(([verb, count], index) =>
            `${verb} ${count}${index ? "" : count === 1 ? " object" : " objects"}`);
        const others = [...worldAssetNouns(item.assets || []), ...(item.settings ? ["the world's settings"] : []),
          ...(item.ground || []).map((name) => `the ground of ${name}`)];
        const list = (words) => words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}` : words[0] || "";
        const text = counts.length ? `${list(counts)} in the world${others.length ? `, with ${list(others)}` : ""}` :
          others.length ? `changed ${list(others)}` : "changed nothing in the world";
        const actions = item.undone ? [h("span", { class: "blip-undone", text: item.undoneText || "Undone." })] : [
          h("button", { type: "button", onclick: () => showWorld(item) }, "Show"),
          h("button", { type: "button", onclick: () => undoWorld(item) }, "Undo"),
        ];
        return h("div", { class: "blip-changes" },
          h("p", { text: `${text[0].toUpperCase()}${text.slice(1)}.` }),
          item.note ? h("p", { class: "blip-undone", text: item.note }) : null,
          h("div", { class: "blip-change-buttons" }, actions));
      }
      case "confirm": {
        // A removal of more than five objects, which waits for the user. One
        // from before the page was loaded again waits no longer.
        const names = item.removes.map((entry) => entry.name || entry.id);
        const shown = names.length > 12 ? [...names.slice(0, 12), `and ${names.length - 12} more`] : names;
        const answered = item.answer || (removalAnswers.has(item) ? null : "expired");
        const actions = answered ? [h("span", { class: "blip-undone", text: { yes: "You said yes.", no: "You said no.",
          stopped: "Stopped: nothing was removed.", expired: "Blip stopped waiting: nothing was removed." }[answered] })] : [
          h("button", { type: "button", class: "blip-primary", onclick: () => answerRemoval(item, true) },
            `Remove ${names.length}`),
          h("button", { type: "button", onclick: () => answerRemoval(item, false) }, "Keep them"),
        ];
        return h("div", { class: "blip-notice" },
          h("p", { text: `Blip wants to remove ${names.length} objects: ${shown.join(", ")}.` }),
          h("div", { class: "blip-change-buttons" }, actions));
      }
      case "notice": {
        const action = {
          reload: ["Reload the page", () => location.reload()],
          newChat: ["Start a new chat", () => newConversation()],
          signIn: ["Sign in", () => ide.openAccount()],
          account: ["Membership…", () => ide.openAccount()],
          // The credit packs page on the IDE's server (web/ide/buy/), in a tab
          // or, in the desktop IDEs, the system browser.
          topup: ["Top up Blip…", () => window.open(new URL("../buy/credits/", cloud.base), "_blank", "noopener")],
          retry: ["Try again", () => retry()],
        }[item.action];
        return h("div", { class: `blip-notice${item.tone === "error" ? " blip-error" : ""}` }, h("p", { text: item.text }),
          action ? h("button", { type: "button", onclick: action[1] }, action[0]) : null);
      }
      case "todo":
        return todoCard(item);
      case "model":
        return modelCard(item);
      case "assets":
        return assetsCard(item);
      case "choices":
        // Answers to Blip's question (offer_choices), until the user answers.
        if (item.answered) return h("div", { hidden: true });
        return h("div", { class: "blip-choices", role: "group", "aria-label": "Answers" }, item.choices.map((choice) =>
          h("button", { type: "button", onclick: () => answerChoice(item, choice) }, choice)));
      default:
        return h("div");
    }
  }

  // ------------------------------------------------------------- models
  // A model's, picture's, sound's or music's card (models.js): what is being
  // made and how far it has got, the energy it uses, a picture of it (a
  // sound's loudness along it), and where it was saved; Make it and Not now
  // for a big one; and with the World Editor open, adding it to the world's
  // assets, and placing it, or a sound playing it, which its play buttons
  // let the user hear first.
  function modelCard(item) {
    const audio = item.jobKind === "sound" || item.jobKind === "music";
    const asking = item.status === "asking" && modelAnswers.has(item);
    const here = ide.project() && ide.project().id === item.project;
    const status = {
      asking: asking ? `Shall I make it? It uses ${percent(item.share)} of my energy for today.` +
        (item.asksSharing ? " Shared, it goes in the shared library for every member to use, and can't be unshared; " +
          `${percent(item.rebate)} of my energy comes back.` : "") : "Not made: Blip stopped waiting.",
      declined: "Not made.",
      running: tools.models.stepText(item),
      done: here ? "Made: saving it…" : "Made: it is saved when its project is open.",
      saved: item.paths && item.paths.length > 1 ? `Saved as ${item.paths[0]} to ${item.paths[item.paths.length - 1]}` :
        `Saved as ${item.path}${item.maps && Object.keys(item.maps).length ? ", with its maps beside it" : ""}`,
      failed: item.error || `${makerOf(item.jobKind)} could not make it.`,
    }[item.status] || "";
    const energy = item.status === "running" ? `Uses ${percent(item.share)} of my energy` :
      item.status === "saved" || item.status === "done" ? `Used ${percent(item.spent ?? item.share)} of my energy` : "";
    const buttons = [];
    let remember = null;
    if (asking && item.asksSharing) {
      // "Remember my answer" keeps it for the next card; the account
      // dialog's Sharing section changes it.
      const box = h("input", { type: "checkbox" });
      remember = h("label", { class: "blip-remember" }, box, " Remember my answer");
      const answer = (share) => () => answerModel(item, { share, remember: box.checked });
      buttons.push(h("button", { type: "button", class: "blip-primary", onclick: answer(true) }, "Make and share"));
      buttons.push(h("button", { type: "button", onclick: answer(false) }, "Make it just for me"));
      buttons.push(h("button", { type: "button", onclick: () => answerModel(item, false) }, "Not now"));
    } else if (asking) {
      buttons.push(h("button", { type: "button", class: "blip-primary", onclick: () => answerModel(item, { share: false }) },
        "Make it"));
      buttons.push(h("button", { type: "button", onclick: () => answerModel(item, false) }, "Not now"));
    } else if (item.status === "saved" && here && ide.project().has(item.path) && audio) {
      soundButtons(item, buttons);
    } else if (item.status === "saved" && here && ide.project().has(item.path)) {
      if (item.jobKind === "picture") {
        buttons.push(h("button", { type: "button", onclick: () => ide.openFile(item.path) }, "Show"));
      } else if (ide.worldEditor() && !item.asset) {
        buttons.push(h("button", { type: "button", onclick: () => addToWorld(item) }, "Add to the world's assets"));
      } else if (ide.worldEditor() && !item.placed) {
        buttons.push(h("button", { type: "button", onclick: () => placeInWorld(item) }, "Place it in the world"));
      }
    }
    return h("div", { class: `blip-model${item.status === "failed" ? " blip-error" : ""}` },
      audio ? waveform(item) : null,
      item.thumb ? h("img", { class: "blip-model-picture", src: item.thumb,
        alt: item.jobKind === "picture" ? item.title : `${item.title}, as Tripo pictured it` }) : null,
      h("div", { class: "blip-model-body" },
        h("p", { class: "blip-model-title" }, h("strong", { text: item.title })),
        // What Tripo or OpenAI was given.
        item.source ? h("p", { class: "blip-model-from" }, h("img", { src: item.source, alt: "" }),
          h("span", { text: item.jobKind === "model" ? "Made from this picture" : "From this picture" })) : null,
        h("p", { class: "blip-model-status", text: status }),
        item.status === "running" ? h("progress", { max: "100", value: String(item.progress || 0),
          "aria-label": `${item.title}, ${item.progress || 0}% made` }) : null,
        energy ? h("p", { class: "blip-model-energy", text: energy }) : null,
        sharedLine(item) ? h("p", { class: "blip-model-energy", text: sharedLine(item) }) : null,
        item.note ? h("p", { class: "blip-undone", text: item.note }) : null,
        buttons.length ? h("div", { class: "blip-change-buttons" }, buttons) : null,
        remember));
  }

  // A sound's loudness along it, from the server's measure (audio.mjs
  // waveOf), as the card's picture; a note while it is being made.
  function waveform(item) {
    const wave = item.sounds && item.sounds[0] && item.sounds[0].wave;
    const box = h("div", { class: "blip-model-wave", "aria-hidden": "true" });
    if (!wave || !wave.length) {
      box.append(h("span", { text: item.jobKind === "music" ? "♫" : "♪" }));
      return box;
    }
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", `0 0 ${wave.length} 40`);
    svg.setAttribute("preserveAspectRatio", "none");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    // Drawn to its own loudest, so that a soft sound's shape shows too.
    const most = Math.max(...wave) || 1;
    path.setAttribute("d", wave.map((value, index) => `M${index + 0.5} ${20 - Math.max(0.5, value / most * 19)}V` +
      `${20 + Math.max(0.5, value / most * 19)}`).join(""));
    svg.append(path);
    box.append(svg);
    return box;
  }

  // A saved sound's buttons: play (each variation), and with the World
  // Editor open, music as the world's music, a loop everywhere in the world,
  // or a one-shot in its assets.
  function soundButtons(item, buttons) {
    const paths = item.paths || [item.path];
    paths.forEach((path, index) => {
      const on = playing && playing.item === item && playing.index === index;
      const label = paths.length > 1 ? `${on ? "■" : "▶"} ${index + 1}` : on ? "■ Stop" : "▶ Play";
      buttons.push(h("button", { type: "button", "aria-label": `${on ? "Stop" : "Play"} ${path}`,
        onclick: () => on ? stopSound() : playSound(item, index) }, label));
    });
    if (!ide.worldEditor()) return;
    if (item.jobKind === "music" && item.worldUse !== "music") {
      buttons.push(h("button", { type: "button", onclick: () => soundInWorld(item, "music") }, "Make it the world's music"));
    } else if (item.jobKind === "sound" && item.loop && item.worldUse !== "bed") {
      buttons.push(h("button", { type: "button", onclick: () => soundInWorld(item, "bed") }, "Play it everywhere"));
    } else if (item.jobKind === "sound" && !item.loop && !item.asset) {
      buttons.push(h("button", { type: "button", onclick: () => addToWorld(item) }, "Add to the world's assets"));
    }
  }

  // The sound playing now, one at a time: { item, index, audio, url }. A
  // loop plays on until it is stopped, so that its join can be heard.
  let playing = null;
  async function playSound(item, index) {
    stopSound();
    const project = ide.project(), path = (item.paths || [item.path])[index];
    if (!project || !project.has(path)) return modelNote(item, `${path} is no longer in the project.`);
    // The desktop window's project gives the file's bytes, not a Blob.
    const bytes = await project.blob(path).arrayBuffer();
    // One sound at a time, even after two quick clicks.
    stopSound();
    const url = URL.createObjectURL(new Blob([bytes], { type: /\.ogg$/i.test(path) ? "audio/ogg" : "audio/wav" }));
    const audio = new Audio(url);
    audio.loop = !!item.loop;
    playing = { item, index, audio, url };
    audio.addEventListener("ended", () => stopSound());
    audio.addEventListener("error", () => {
      stopSound();
      modelNote(item, "This browser can't play it, but your game can.");
    });
    audio.play().catch(() => {});
    update(item);
  }

  function stopSound() {
    if (!playing) return;
    const { item, audio, url } = playing;
    playing = null;
    audio.pause();
    URL.revokeObjectURL(url);
    update(item);
  }

  // A sound in the world, as world_action soundscape puts one: music as the
  // world's music, or a loop as a bed of its soundscape, heard everywhere.
  async function soundInWorld(item, how) {
    if (busy) return modelNote(item, "Wait until Blip has finished, then add it.");
    if (!ide.worldEditor()) return modelNote(item, "Open the World Editor to add it to a world.");
    try {
      tools.world.begin();
      await tools.world.action(how === "music" ?
        { name: "soundscape", music: item.path, label: `Make ${item.title} the world's music` } :
        { name: "soundscape", beds: (item.paths || [item.path]).slice(0, 1).map((path) => ({ path })), add: true,
          label: `Play ${item.title} everywhere` });
      const step = await tools.world.finishRequest();
      item.worldUse = how;
      item.note = how === "music" ? "It is the world's music: it plays when the world's sounds start, as in the editor's Test." :
        "It plays everywhere in the world, behind its other sounds.";
      update(item);
      if (step) add({ kind: "world", ...step, undone: false, note: "" });
      await save();
      ide.status(`${item.title} is in the world. Save the world in the editor to keep it.`);
    } catch (error) {
      await tools.world.finishRequest().catch(() => null);
      await modelNote(item, error.message || String(error));
    }
  }

  // Where a shared job's model or picture is on its way into the library.
  function sharedLine(item) {
    const state = item.shareState;
    if (!state) return item.shared && item.status === "running" ? "It goes in the shared library when it is made." : "";
    switch (state.status) {
      case "waiting": case "making": return "Going in the shared library…";
      case "checking": return "Being checked for the shared library…";
      case "held": return "Shared: a moderator looks at it before it is in the library.";
      case "public": return `In the shared library.${state.rebate ? ` ${percent(state.rebate)} of my energy came back.` : ""}`;
      case "refused": return `Not in the shared library: ${state.reason || "it broke the library's rules"}.`;
      case "failed": return state.reason || "It couldn't be shared.";
      default: return "";
    }
  }

  // Shared assets that find_assets found: each with its thumbnail, its
  // maker, and Add, which saves it in the project as add_asset does.
  function assetsCard(item) {
    const here = ide.project() && ide.project().id === item.project;
    return h("div", { class: "blip-changes" },
      h("p", { text: `From the shared library${item.query ? `, for "${item.query}"` : ""}:` }),
      h("div", { class: "blip-assets" }, item.assets.map((asset) => {
        const added = item.added[asset.id];
        return h("div", { class: "blip-asset" },
          asset.thumbnail ? h("img", { src: asset.thumbnail, alt: asset.name, loading: "lazy" }) : null,
          h("p", {}, h("strong", { text: asset.name }), h("br"), h("span", { class: "blip-undone", text: `by ${asset.maker}` })),
          added ? h("p", { class: "blip-undone", text: added }) : here ?
            h("button", { type: "button", onclick: () => addShared(item, asset) }, "Add") : null);
      })));
  }

  async function addShared(item, asset) {
    item.added[asset.id] = "Adding…";
    update(item);
    try {
      const text = await tools.models.addAsset({ id: asset.id });
      const saved = /as (\S+?) \(/.exec(text);
      item.added[asset.id] = saved ? `Added as ${saved[1]}` : "Added";
      ide.refreshFiles();
    } catch (error) {
      item.added[asset.id] = error.message || String(error);
    }
    update(item);
    chats.put(owners.get(item) || chat);
  }

  // Only the user's click starts a big job, or one that could be shared;
  // Stop answers no. The answer is false, or { share, remember }.
  const modelAnswers = new WeakMap();      // card -> resolve, while it asks
  function confirmModel(item, signal) {
    return new Promise((resolve) => {
      if (signal && signal.aborted) return resolve(false);
      modelAnswers.set(item, resolve);
      update(item);
      // He comes back with his panel to ask, if the user closed it.
      if (!panelOpen()) open({ focus: false });
      if (signal) signal.addEventListener("abort", () => answerModel(item, false), { once: true });
    });
  }

  function answerModel(item, answer) {
    const resolve = modelAnswers.get(item);
    if (!resolve) return;
    modelAnswers.delete(item);
    update(item);
    resolve(answer);
  }

  function answerChoice(item, choice) {
    if (busy) return;
    item.answered = choice;
    update(item);
    ask(choice);
  }

  // The chat each card belongs to, so that a job that ends after the user
  // moved to another project is kept in its own.
  const owners = new WeakMap();

  function modelNote(item, note) {
    item.note = note;
    update(item);
    return chats.put(owners.get(item) || chat);
  }

  // A model made while the World Editor is open, as an asset of its world.
  async function addToWorld(item) {
    if (busy) return modelNote(item, "Wait until Blip has finished, then add it.");
    if (!ide.worldEditor()) return modelNote(item, "Open the World Editor to add it to a world.");
    try {
      tools.world.begin();
      const { asset } = await tools.world.importFile(item.path, `Import ${item.title}`);
      const step = await tools.world.finishRequest();
      item.asset = asset;
      item.note = `In the world's assets as ${asset}.`;
      update(item);
      if (step) add({ kind: "world", ...step, undone: false, note: "" });
      await save();
      ide.status(`${item.title} is in the world's assets. Save the world in the editor to keep it.`);
    } catch (error) {
      await tools.world.finishRequest().catch(() => null);
      await modelNote(item, error.message || String(error));
    }
  }

  async function placeInWorld(item) {
    if (busy) return modelNote(item, "Wait until Blip has finished, then place it.");
    if (!ide.worldEditor()) return modelNote(item, "Open the World Editor to place it.");
    try {
      tools.world.begin();
      await tools.world.action({ name: "add-model", asset: item.asset, objectName: item.title, label: `Place ${item.title}` });
      const step = await tools.world.finishRequest();
      item.placed = true;
      item.note = `Placed in the world at the editor's focus point.`;
      update(item);
      if (step) add({ kind: "world", ...step, undone: false, note: "" });
      await save();
      ide.status(`${item.title} is in the world. Save the world in the editor to keep it.`);
    } catch (error) {
      await tools.world.finishRequest().catch(() => null);
      await modelNote(item, error.message || String(error));
    }
  }

  // Follows the open project's cards whose jobs had not ended, or whose
  // results are not saved yet, as when the page was closed while they were
  // being made.
  function resumeModels() {
    if (!chat || !signedIn()) return;
    const owner = chat;
    for (const item of owner.log) {
      if (item.kind !== "model") continue;
      owners.set(item, owner);
      if (item.status === "asking" && !modelAnswers.has(item)) {
        item.status = "declined";
        item.note = "Blip stopped waiting.";
        update(item);
        chats.put(owner);
      } else if (item.status === "running" || item.status === "done" && !item.path) {
        tools.models.follow(item).catch((error) => console.error(error));
      }
    }
  }

  // A job ended: its card is kept, and Blip shows how it went when nothing
  // else is going on.
  async function modelFinished(item) {
    await chats.put(owners.get(item) || chat);
    if (busy || owners.get(item) !== chat) return;
    await refreshUsage();
    if (item.status === "saved") {
      figure.set("pleased");
    } else if (item.status === "failed") {
      figure.set("surprised");
    }
  }

  // What Blip's context says of models: whether he can make them, the
  // energy left, and those still being made.
  function modelsLine() {
    const library = tools.models.libraryAvailable() ? " The shared asset library is there (find_assets, add_asset)." : "";
    if (!tools.models.available()) return `Making 3D models, pictures, sounds and music is not available here.${library}`;
    const kinds = [tools.models.available("model") && "3D models", tools.models.available("picture") && "pictures and textures",
      tools.models.available("sound") && "sound effects", tools.models.available("music") && "music"];
    const names = ["3D models", "pictures", "sound effects", "music"];
    const absent = names.filter((name, index) => !kinds[index]);
    const missing = absent.length ? ` Making ${absent.join(", ").replace(/, ([^,]*)$/, " or $1")} is not available here.` : "";
    const left = usage && usage.allowance > 0 ? Math.max(0, 1 - usage.used / usage.allowance) : null;
    const making = (chat ? chat.log : []).filter((item) => item.kind === "model" && (item.status === "running" ||
      item.status === "done" && !item.path)).map((item) => `${item.title} (${item.status === "done" ? "made, not saved yet" :
      `${item.progress || 0}%`})`);
    return `Blip can have ${kinds.filter(Boolean).join(", ").replace(/, ([^,]*)$/, " and $1")} made here${left === null ? "" :
      `: ${percent(left)} of today's energy is left`}.${missing}` + (making.length ? ` Being made now: ${making.join(", ")}.` : "") + library;
  }

  const ended = new WeakSet();      // cards whose end was dealt with
  tools.models.panel = {
    add(item) {
      owners.set(item, chat);
      return add(item);
    },
    // The effort the user picked, which chooses how pictures are drawn.
    level: () => level,
    update(item) {
      update(item);
      if (["saved", "failed", "declined"].includes(item.status) && !ended.has(item)) {
        ended.add(item);
        modelFinished(item);
      }
    },
    confirm: confirmModel,
    progress(text) {
      if (busy) progress(text);
    },
    energyChanged: () => refreshUsage(),
    choices: (choices) => add({ kind: "choices", choices, answered: null }),
    // What find_assets found in the shared library, with Add buttons.
    assets(item) {
      owners.set(item, chat);
      return add(item);
    },
    chat: () => chat && chat.id,
    // The user's pictures in the chat, by number, for the tools.
    picture: (n) => chatPicture(chat, n),
    pictureCount: () => shownPictures(chat).length,
  };
  // The TODO notes' (todo.js): the user's pictures, who wrote the note, and
  // the world open in the World Editor.
  tools.notes.panel = {
    picture: (n) => chatPicture(chat, n),
    pictureCount: () => shownPictures(chat).length,
    about: () => {
      const entry = levelOf(level) || levels[normal];
      const editor = ide.worldEditor();
      return { by: `Blip at ${entry.level} effort (${entry.model})`, world: editor && editor.path || null };
    },
  };

  const elements = new WeakMap();
  function render() {
    log.replaceChildren(...(chat ? chat.log : []).map((item) => {
      const element = itemElement(item);
      elements.set(item, element);
      return element;
    }));
    renderStarters();
    log.scrollTop = log.scrollHeight;
  }
  function add(item) {
    chat.log.push(item);
    const element = itemElement(item);
    elements.set(item, element);
    log.append(element);
    renderStarters();
    log.scrollTop = log.scrollHeight;
    return item;
  }
  const pending = new Set();
  let flushing = false;
  function update(item) {
    // At most once a frame as text streams in, or every 50 ms while the
    // page is hidden, where frames do not come.
    pending.add(item);
    if (flushing) return;
    flushing = true;
    const flush = () => {
      flushing = false;
      const items = [...pending];
      pending.clear();
      const atEnd = log.scrollHeight - log.scrollTop - log.clientHeight < 40;
      for (const changed of items) {
        const old = elements.get(changed);
        if (!old || !old.isConnected) continue;
        const element = itemElement(changed);
        elements.set(changed, element);
        old.replaceWith(element);
      }
      if (atEnd) log.scrollTop = log.scrollHeight;
    };
    if (document.visibilityState === "hidden") setTimeout(flush, 50);
    else requestAnimationFrame(flush);
  }

  function renderStarters() {
    renderGuides();
    const empty = !chat || !chat.log.length;
    const list = [];
    if (empty && canAsk() && !busy && ide.worldEditor()) {
      // The sketch pad, with a hint, and "Build this." to send with it.
      list.push(["Build from a drawing", () => openSketch(true)]);
      list.push(["Explain this world", "Explain what is in this world, simply."]);
      list.push(["Add some lighting", "Add some lighting to this world."]);
      list.push(["Tidy up the names", "Give the objects in this world clear names."]);
      if (tools.models.available("model")) list.push(["Make a 3D model", "Make a 3D model for this world."]);
    } else if (empty && canAsk() && !busy) {
      const last = ide.lastRun();
      if (last && last.ended && last.ended.status === "compile-error") list.push(["Fix this error", "Please fix the errors in my program."]);
      else if (last && last.ended && typeof last.ended.status === "number" && last.ended.status !== 0) {
        list.push(["Fix this error", "My program stopped with a runtime error. Please fix it."]);
      }
      if (ide.editor.selectedText) list.push(["Ask about the selected code", "Explain the code I've selected, simply."]);
      list.push(["Explain this program", "Explain what this program does, simply."]);
      list.push(["Add a score", "Add a score to my program, shown on the screen."]);
      list.push(["Make it look better", "Make my program look better."]);
      if (tools.models.available("model")) list.push(["Make a 3D model", "Make a 3D model for my game."]);
    }
    starters.replaceChildren(...list.map(([label, text]) => h("button", { type: "button",
      onclick: () => typeof text === "function" ? text() : ask(text) }, label)));
    starters.hidden = !list.length;
  }

  // ---------------------------------------------------------- the account
  function accountChanged() {
    signInNote.hidden = canAsk();
    form.hidden = !canAsk();
    effortRow.hidden = !canAsk();
    renderStarters();
    refreshUsage();
    resumeModels();
  }

  async function refreshUsage() {
    if (!canAsk()) {
      energy.hidden = true;
      return;
    }
    try {
      usage = await cloud.request("GET", "blip/usage");
      const left = usage.allowance > 0 ? Math.max(0, 1 - usage.used / usage.allowance) : 0;
      energyBar.style.width = `${Math.round(left * 100)}%`;
      const renews = new Date(usage.renews).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      const plan = usage.membership;
      energy.title = plan ? planTitle(plan) :
        `Blip's energy for today: ${Math.round(left * 100)}% left. It fills up again at ${renews}.`;
      bought.hidden = !(plan && plan.bought.credits > 0);
      if (plan) {
        bought.textContent = `+${plan.bought.credits}`;
        bought.title = plan.bought.usable ? `${plan.bought.credits} bought credits, used once the week's run out` :
          `${plan.bought.credits} bought credits, used while you're a member`;
      }
      energy.setAttribute("aria-label", energy.title);
      energy.hidden = false;
      if (!usage.left && !busy) {
        figure.set("sleepy");
        presence("sleepy");
      } else if (figure.state === "sleepy") {
        figure.set("resting");
        presence("idle");
      }
    } catch {
      energy.hidden = true;
    }
  }

  // ---------------------------------------------------------- the project
  async function projectChanged() {
    // A request for the last project ends first, and keeps its record there.
    if (busy) {
      busy.controller.abort();
      await busy.done;
    }
    const project = ide.project();
    chat = project ? await chats.get(project.id) : null;
    render();
    resumeModels();
  }

  async function ensureChat() {
    const project = ide.project();
    if (!chat || chat.project !== project.id) chat = (await chats.get(project.id)) || newChat(project.id, pack);
    return chat;
  }

  async function newConversation() {
    if (busy) return;
    const project = ide.project();
    if (!project) return;
    chat = newChat(project.id, pack);
    await save();
    ide.editor.clearChangeMarks();
    render();
    input.focus();
  }

  // ------------------------------------------------------------- changes
  async function currentText(path) {
    const project = ide.project();
    if (!project.has(path)) return null;
    const open = ide.editor.textOf(path);
    if (open !== undefined) return open;
    return (await project.text(path)).replace(/\r\n?/g, "\n");
  }

  async function showChanges(item) {
    const first = item.changes.find((change) => ide.project().has(change.path));
    // A change of the main file alone shows the file Run starts now.
    if (!first) {
      if (item.main && ide.project().has(item.main.after)) await ide.openFile(item.main.after);
      return;
    }
    for (const change of item.changes) {
      if (!ide.project().has(change.path)) continue;
      ide.editor.ensure(change.path, await ide.project().text(change.path));
      const now = await currentText(change.path);
      ide.editor.markChanges(change.path, changedRows(change.before, now));
    }
    await ide.openFile(first.path);
    const rows = changedRows(first.before, await currentText(first.path));
    if (rows.length) ide.editor.goTo(rows[0]);
  }

  async function undo(item) {
    if (busy) return;
    const kept = [];
    // The main file goes back first, while the file it was is still there,
    // unless the user has chosen another since.
    let mainKept = false;
    if (item.main) {
      const project = ide.project();
      if (project.main !== item.main.after) mainKept = project.main !== item.main.before;
      else if (item.main.before && project.has(item.main.before)) await ide.setMain(item.main.before);
      else {
        await project.update({ main: null });
        ide.refreshFiles();
      }
    }
    for (const change of item.changes) {
      const now = await currentText(change.path);
      if (now !== change.after) {
        if (now !== null) kept.push(change.path);
        continue;
      }
      if (change.before === null) await ide.removeFile(change.path);
      else ide.editor.edit(change.path, now, { from: 0, to: now.length, insert: change.before });
    }
    ide.editor.clearChangeMarks();
    item.undone = true;
    item.undoneText = kept.length ? `Undone, except ${kept.join(", ")}, which changed since: Ctrl+Z there undoes Blip's ` +
      "edits one at a time." : item.changes.length ? "Undone: the files are as they were before." : "Undone.";
    if (item.main && !mainKept) item.undoneText += ` Run starts ${item.main.before || "no file"} again.`;
    else if (mainKept) item.undoneText += " The file Run starts was changed since, so it stays.";
    update(item);
    await save();
    ide.status(item.undoneText);
  }

  // ------------------------------------------------------------ TODO notes
  // A message that starts with TODO is a note for later (todo.js): it can
  // go only where there is a folder for it, which the desktop IDE has with
  // a project open, or a folder chosen for notes.
  const todoFolder = () => (ide.todoFolder && ide.todoFolder()) || null;
  const canSaveTodo = () => !ide.desktop || !!(ide.folder && ide.folder()) || !!todoFolder();

  // The line under the message box while it holds a TODO note.
  function todoHint() {
    if (!canSaveTodo()) {
      return "A TODO note needs a project: open one first (File > Open Project...), or choose a folder for notes.";
    }
    const session = ide.buddies && ide.buddies();
    return `A TODO note: Blip writes it up in ${todoFolder() || "todo/"} and changes nothing now.` +
      (session && session.you ? " Everyone in this session will see it." : "");
  }

  // Where notes go, beside the line, where the desktop IDE can keep them in
  // a folder of the user's choosing: the project's todo folder, the folder
  // chosen, or another, which the IDE's own folder picker chooses.
  function todoFolderList() {
    const chosen = todoFolder();
    const list = h("select", { class: "blip-todo-folder", "aria-label": "Where TODO notes go",
      onchange: async () => {
        const value = list.value;
        list.disabled = true;
        try {
          if (value === "choose") await ide.chooseTodoFolder();
          else if (value === "" && chosen) await ide.clearTodoFolder();
        } catch (error) {
          noteGuides(error.message || String(error));
        }
        renderGuides();
      } },
    h("option", { value: "", text: "In the project's todo folder" }),
    chosen ? h("option", { value: chosen, text: `In ${chosen}` }) : null,
    h("option", { value: "choose", text: "In a folder I choose…" }));
    list.value = chosen || "";
    return list;
  }

  // What the IDE can do with a note's file: the online IDE opens it, and the
  // desktop IDE opens it and shows its folder once it says it can
  // (hello.features, desktop.js).
  const hostCan = (feature) => ide.desktop ? !!(ide.features && ide.features().includes(feature)) : feature === "open";

  // Why Blip didn't write a note up, as its card and its last line say it.
  function todoReason(outcome, failure) {
    if (failure instanceof BlipError) {
      if (failure.code === "allowance" || failure.code === "global") return "his energy has run out for now";
      if (failure.code === "connection") return "he couldn't reach the server";
      const first = (failure.message || "").split(/(?<=\.)\s/)[0].replace(/\.$/, "");
      return first ? `${first[0].toLowerCase()}${first.slice(1)}` : "something went wrong";
    }
    if (failure) return "something went wrong";
    switch (outcome && outcome.reason) {
      case "refusal": return "he couldn't help with it";
      case "max_tokens": return "his answer was cut off";
      case "steps": return "he took too many steps";
      default: return "he ended without saving it";
    }
  }

  // The log item of the notes a request saved, with the thumbnails of their
  // pictures, as the user's messages show them.
  function todoItem(asTyped) {
    const thumbs = new Map();
    for (const item of chat.log) for (const picture of item.kind === "user" && item.pictures || []) thumbs.set(picture.n, picture.thumb);
    return { kind: "todo", project: ide.project().id, asTyped, undone: false, note: "",
      notes: tools.notes.saved.map((record) => ({ ...record,
        pictures: record.pictures.map((picture) => ({ ...picture, thumb: thumbs.get(picture.n) || null })) })) };
  }

  // A note's card: each note, its path and pictures, Open, Copy path and
  // Show in folder; Undo; and for a note saved as typed, Ask Blip to add to
  // it.
  function todoCard(item) {
    const heading = item.asTyped ? `Saved as you typed it: Blip couldn't add to it, because ${item.asTyped}.` : "Noted for later:";
    const notes = item.notes.map((note) => {
      const buttons = [];
      if (!item.undone && hostCan("open")) buttons.push(h("button", { type: "button", onclick: () => openNote(item, note) }, "Open"));
      if (!item.undone && note.full && navigator.clipboard) {
        buttons.push(h("button", { type: "button", onclick: () => copyNotePath(item, note) }, "Copy path"));
      }
      if (!item.undone && note.full && hostCan("reveal") && ide.revealFile) {
        buttons.push(h("button", { type: "button", onclick: () => revealNote(item, note) }, "Show in folder"));
      }
      const pictures = note.pictures.filter((picture) => picture.thumb);
      return h("div", { class: "blip-todo-note" },
        h("p", {}, h("strong", { text: note.title })),
        h("p", { class: "blip-todo-path", text: note.path }),
        pictures.length ? h("div", { class: "blip-user-pictures" }, pictures.map((picture) =>
          h("button", { type: "button", class: "blip-thumb", title: `Picture ${picture.n}: show it large`,
            onclick: () => showPicture(picture.n) }, h("img", { src: picture.thumb, alt: `Picture ${picture.n}` })))) : null,
        buttons.length ? h("div", { class: "blip-change-buttons" }, buttons) : null);
    });
    const actions = item.undone ? [h("span", { class: "blip-undone", text: item.undoneText || "Undone." })] : [
      item.asTyped ? h("button", { type: "button", onclick: () => ask(`Add to the TODO note ${item.notes[0].path}: fill in ` +
        "what you can from this chat and its pictures, as save_todo would, and keep its As written part as it is.") },
      "Ask Blip to add to it") : null,
      h("button", { type: "button", onclick: () => undoTodo(item) }, "Undo"),
    ];
    return h("div", { class: "blip-changes blip-todo" }, h("p", { text: heading }), notes,
      item.note ? h("p", { class: "blip-undone", text: item.note }) : null,
      h("div", { class: "blip-change-buttons" }, actions));
  }

  function todoNote(item, note) {
    item.note = note;
    update(item);
    return save();
  }

  // Why a card's note can't be reached now, or "".
  function noteAway(item, note) {
    const project = ide.project();
    if (!project || project.id !== item.project) return "Open the project it was saved in first.";
    if (note && !project.has(note.path)) return `${note.path} is no longer in the project.`;
    return "";
  }

  async function openNote(item, note) {
    const away = noteAway(item, note);
    if (away) return todoNote(item, away);
    try {
      await ide.openFile(note.path);
    } catch (error) {
      await todoNote(item, error.message || String(error));
    }
  }

  async function copyNotePath(item, note) {
    try {
      await navigator.clipboard.writeText(note.full);
      await todoNote(item, `Copied ${note.full}`);
    } catch {
      await todoNote(item, `Its path: ${note.full}`);
    }
  }

  async function revealNote(item, note) {
    const away = noteAway(item, note);
    if (away) return todoNote(item, away);
    try {
      await ide.revealFile(note.path);
    } catch (error) {
      await todoNote(item, error.message || String(error));
    }
  }

  // Undo takes the notes and their pictures away, but not a note changed
  // since, nor the folder's README.
  async function undoTodo(item) {
    if (busy) return todoNote(item, "Wait until Blip has finished, then undo it.");
    const away = noteAway(item, null);
    if (away) return todoNote(item, away);
    try {
      const { removed, kept } = await tools.notes.undo(item.notes);
      if (kept.length && !removed.length) {
        return todoNote(item, `${kept.join(", ")} changed since, so it stays: delete it from the todo folder yourself if it ` +
          "should go.");
      }
      item.undone = true;
      item.undoneText = kept.length ? `Undone, except ${kept.join(", ")}, which changed since.` :
        `Undone: ${item.notes.length === 1 ? "the note and its pictures are" : "the notes and their pictures are"} gone.`;
      item.note = "";
      update(item);
      await save();
      ide.status(item.undoneText);
    } catch (error) {
      await todoNote(item, error.message || String(error));
    }
  }

  // --------------------------------------------------------- world changes
  // The card's Show and Undo work while the World Editor is open on the
  // world he changed.
  function worldNote(item, note) {
    item.note = note;
    update(item);
    return save();
  }

  // Why the card cannot reach its world now, or "".
  function worldAway(item, what) {
    const editor = ide.worldEditor();
    const name = item.world || "this world";
    if (!editor) return `Open the World Editor on ${name} to ${what} it.`;
    if (item.world && editor.path && item.world !== editor.path) return `The World Editor is on another world: open ${name} to ${what} it.`;
    return "";
  }

  async function showWorld(item) {
    const away = worldAway(item, "see");
    if (away) return worldNote(item, away);
    try {
      await tools.world.show(item);
      await worldNote(item, "");
    } catch (error) {
      await worldNote(item, error.message || String(error));
    }
  }

  async function undoWorld(item) {
    if (busy) return worldNote(item, "Wait until Blip has finished, then undo it.");
    const away = worldAway(item, "undo");
    if (away) return worldNote(item, away);
    try {
      const result = await tools.world.undo(item);
      if (!result.ok) return worldNote(item, result.error);
      item.undone = true;
      item.undoneText = "Undone: the world is as it was before Blip's change. Edit > Undo in the editor brings it back.";
      item.note = "";
      update(item);
      await save();
      ide.status("Blip's change to the world is undone. Save the world in the editor to keep it.");
    } catch (error) {
      await worldNote(item, error.message || String(error));
    }
  }

  // A removal of more than five objects waits for the user's yes, which only
  // their click gives. Stop answers no.
  function confirmRemoval(removes, signal) {
    return new Promise((resolve) => {
      if (signal && signal.aborted) return resolve(false);
      const item = { kind: "confirm", removes: removes.map(({ id, name }) => ({ id, name })), answer: null };
      removalAnswers.set(item, resolve);
      add(item);
      save();
      if (hooks.confirm) hooks.confirm(item.removes);
      // He comes back with his panel to ask, if the user closed it.
      if (!panelOpen()) open({ focus: false });
      if (signal) signal.addEventListener("abort", () => answerRemoval(item, false, "stopped"), { once: true });
    });
  }

  function answerRemoval(item, yes, answer = yes ? "yes" : "no") {
    if (item.answer || !removalAnswers.has(item)) return;
    item.answer = answer;
    const resolve = removalAnswers.get(item);
    removalAnswers.delete(item);
    update(item);
    save();
    if (resolve) resolve(yes);
  }
  tools.world.ide.confirmRemoval = confirmRemoval;

  // ------------------------------------------------------------- pictures
  // The pictures the user shows him (guides.js): at most three a message,
  // and the chat's 24 shared with his tools' own, of which the older go to
  // make room (agent.js, makeRoom).
  const roomLeft = () => pictureRoom(chat) - guides.length - preparing;

  function noteGuides(text) {
    guideNote = text;
    renderGuides();
  }

  // The button's, a paste's or a drop's files, as many as fit.
  function addGuides(files) {
    let refused = 0;
    for (const file of files) {
      if (guides.length + preparing >= MESSAGE_PICTURES || roomLeft() <= 0) ++refused;
      else addGuide(file);
    }
    if (refused) noteGuides(roomLeft() <= 0 ? FULL : THREE);
  }

  // Every way in ends here: the picture button, pasting and dropping.
  async function addGuide(file, name = file.name) {
    if (roomLeft() <= 0) return noteGuides(FULL);
    if (guides.length + preparing >= MESSAGE_PICTURES) return noteGuides(THREE);
    ++preparing;
    guideNote = "";
    renderGuides();
    try {
      const picture = await prepareGuide(file, name);
      guides.push(picture);
    } catch (error) {
      if (!(error instanceof GuideError)) console.error(error);
      guideNote = error instanceof GuideError ? error.message : CANNOT_OPEN;
      // Not without the picture the user meant to send.
      sendWhenReady = false;
    } finally {
      --preparing;
      renderGuides();
      if (sendWhenReady && !preparing) {
        sendWhenReady = false;
        sendMessage();
      }
    }
  }

  // A drawing from the sketch pad, prepared already.
  function addPrepared(picture) {
    if (roomLeft() <= 0) return noteGuides(FULL);
    if (guides.length + preparing >= MESSAGE_PICTURES) return noteGuides(THREE);
    guides.push(picture);
    noteGuides("");
  }

  // The pictures for the message being sent, out of the row.
  function takeGuides() {
    const taken = guides;
    guides = [];
    guideNote = "";
    renderGuides();
    return taken;
  }

  // The row over the message box: each picture to be sent with a × to take
  // it out, and what to know about the limits.
  function renderGuides() {
    const room = roomLeft();
    const note = guideNote || (room > 0 && room < 3 && canAsk() ?
      `This chat has room for ${room} more ${room === 1 ? "picture" : "pictures"}.` : "");
    const thumbs = guides.map((picture, index) => h("span", { class: "blip-guide" },
      h("button", { type: "button", class: "blip-thumb", title: `${picture.name}: show it large`,
        onclick: () => showPicture(null, picture) },
      h("img", { src: picture.thumb, alt: picture.sketch ? "Your sketch on the world" : picture.name })),
      h("button", { type: "button", class: "blip-guide-remove", title: "Take this picture out", "aria-label": `Take ${picture.name} out`,
        onclick: () => {
          guides.splice(index, 1);
          guideNote = "";
          renderGuides();
        } }, "×")));
    if (preparing) thumbs.push(h("span", { class: "blip-guide blip-guide-waiting", text: "…", title: "Preparing the picture" }));
    if (note) thumbs.push(h("p", { class: "blip-guides-note", role: "status", text: note }));
    // What becomes of a TODO note, before it is sent.
    todoShown = isTodo(input.value) && canAsk();
    if (todoShown) thumbs.push(h("p", { class: "blip-guides-note blip-todo-hint", role: "status", text: todoHint() }));
    if (todoShown && hostCan("todo-folder")) thumbs.push(todoFolderList());
    guideRow.replaceChildren(...thumbs);
    guideRow.hidden = !thumbs.length;
    pictureButton.disabled = guides.length + preparing >= MESSAGE_PICTURES;
    sketchButton.hidden = !ide.worldEditor();
  }

  // A picture large in the panel: the user's picture n of the chat, or one
  // waiting to be sent. Esc or a click closes it.
  function showPicture(n, waiting = null) {
    const picture = waiting || chatPicture(chat, n);
    if (!picture) return;
    closeViewer();
    viewerUrl = URL.createObjectURL(new Blob([picture.bytes], { type: picture.type }));
    viewerPicture.src = viewerUrl;
    viewerPicture.alt = n ? `Picture ${n}` : picture.name;
    const size = picture.width ? `, ${picture.width} x ${picture.height}` : "";
    viewerCaption.textContent = `${n ? `Picture ${n}: ` : ""}${picture.sketch ? "your sketch on the world" : picture.name}${size}` +
      `${n ? "" : ", not sent yet"}. Click or press Esc to close.`;
    viewer.hidden = false;
    viewer.focus();
  }

  function closeViewer() {
    if (viewer.hidden) return false;
    viewer.hidden = true;
    viewerPicture.removeAttribute("src");
    if (viewerUrl) URL.revokeObjectURL(viewerUrl);
    viewerUrl = null;
    return true;
  }

  // The sketch pad over the world seen from above (guides.js SketchPad).
  // From the starter, it says what to do, and its drawing goes with "Build
  // this." unless the user has written something.
  function openSketch(starter = false) {
    if (sketch || !ide.worldEditor() || !canAsk()) return;
    if (roomLeft() <= 0) return noteGuides(FULL);
    if (guides.length + preparing >= MESSAGE_PICTURES) return noteGuides(THREE);
    open();
    closeViewer();
    sketch = new SketchPad({
      topView: (options) => tools.world.topView(options),
      hint: starter ? "Draw where things go, and label them. Blip builds it." : "",
      done: (picture) => {
        closeSketch();
        addPrepared(picture);
        if (starter && !input.value.trim()) input.value = "Build this.";
        input.focus();
      },
      cancel: () => {
        closeSketch();
        input.focus();
      },
    });
    panel.classList.add("blip-sketching");
    panel.append(sketch.element);
    placePanel();
    sketch.load();
    sketch.element.querySelector(".blip-sketch-tool").focus();
  }

  function closeSketch() {
    if (!sketch) return;
    sketch.close();
    sketch.element.remove();
    sketch = null;
    panel.classList.remove("blip-sketching");
    placePanel();
  }

  // Dropping a picture file anywhere on the panel, or one of the project's
  // from the Files pane.
  const dropped = (event) => event.dataTransfer && canAsk() && !sketch &&
    [...event.dataTransfer.types].some((type) => type === "Files" || type === PROJECT_FILE);
  panel.addEventListener("dragover", (event) => {
    if (!dropped(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    dropHint.hidden = false;
  });
  panel.addEventListener("dragleave", (event) => {
    if (!panel.contains(event.relatedTarget)) dropHint.hidden = true;
  });
  panel.addEventListener("drop", (event) => {
    dropHint.hidden = true;
    if (!dropped(event)) return;
    event.preventDefault();
    const path = event.dataTransfer.getData(PROJECT_FILE);
    const project = ide.project();
    if (path && project && project.has(path)) addGuide(project.blob(path), path.split("/").pop());
    else addGuides([...event.dataTransfer.files]);
  });
  // Pasting a picture into the message box, as in chat apps; text pastes as
  // it did.
  input.addEventListener("paste", (event) => {
    const data = event.clipboardData;
    if (!data || !data.files.length) return;
    const text = data.types.includes("text/plain");
    const files = [...data.files].filter((file) => !text || file.type.startsWith("image/"));
    if (!files.length) return;
    if (!text) event.preventDefault();
    addGuides(files);
  });

  // ------------------------------------------------------------ a request
  let lastRequest = null;
  async function retry() {
    if (lastRequest) await ask(lastRequest, { again: true });
  }

  // label: what the panel, and a buddy session's log, show of a request whose
  // words for Claude are long, such as Catch me up's. extra: more of the IDE's
  // context, after the <ide> block's, such as what the user pointed at as
  // they spoke (speech.js pointingContext). pictures: the pictures the user
  // shows with it (guides.js), which go after its words; with no words, it
  // says it is a picture, so that he asks what it is for.
  async function ask(text, { again = false, label = null, extra = null, pictures = [] } = {}) {
    text = text.trim();
    const words = text;
    if (!text && pictures.length) text = pictures.length === 1 ? "Here is a picture." : "Here are some pictures.";
    if (!text || busy || !ide.project()) return;
    if (!canAsk()) {
      accountChanged();
      open();
      return;
    }
    open();
    bubble(null);
    await ensureChat();
    lastRequest = text;
    input.value = "";
    ide.editor.clearChangeMarks();
    // Answers offered for an earlier question go once the user has answered.
    for (const item of chat.log) {
      if (item.kind === "choices" && !item.answered) {
        item.answered = text;
        update(item);
      }
    }
    // Again, after a failure, the conversation is sent as it is when it
    // still ends with the user's message.
    const last = chat.messages[chat.messages.length - 1];
    const resend = again && last && last.role === "user";
    // Numbered on from the chat's pictures; any past its room stay out.
    const first = shownPictures(chat).length + 1;
    if (pictures.length > pictureRoom(chat)) {
      pictures = pictures.slice(0, Math.max(0, pictureRoom(chat)));
      noteGuides(FULL);
    }
    const shown = resend ? [] : pictures.map((picture, index) => ({ n: first + index, thumb: picture.thumb, name: picture.name,
      width: picture.width, height: picture.height, ...(picture.sketch ? { sketch: true } : {}) }));
    // A TODO note (todo.js): its pictures' files, for the note's full-size
    // copies, are kept only for this request.
    const todo = isTodo(words);
    const own = shown.map((entry, index) => ({ n: entry.n, original: pictures[index].original || null, name: entry.name }));
    if (!resend) add({ kind: "user", text: label || words, ...(todo ? { todo: true } : {}), ...(shown.length ? { pictures: shown } : {}) });
    const controller = new AbortController();
    let finished;
    busy = { controller, done: new Promise((resolve) => { finished = resolve; }) };
    setBusy(true);
    if (hooks.started) hooks.started(label || text);
    figure.set("thinking");
    progress("Thinking…");
    presence("thinking", "Thinking…");
    // In a buddy session he answers one person at a time (buddies/panel.js).
    let turn = null, answer = "";
    try {
      turn = ide.blipTurn ? await ide.blipTurn(label || text, controller.signal,
        (name) => progress(`Waiting: Blip is answering ${name} first…`), pictures.slice(0, shown.length)) : null;
    } catch {
      add({ kind: "notice", text: "Stopped." });
      busy = null;
      setBusy(false);
      progress(null);
      presence("idle");
      finished();
      return;
    }
    if (turn) progress("Thinking…");
    tools.begin({ todo, words, pictures: own });
    let steps = null, reply = null;
    const stepOf = new Map();       // a call's id -> its line in the steps
    const events = {
      thinking(snapshot) {
        const note = lastLine(snapshot);
        if (note) progress(note);
        if (figure.state !== "thinking" && figure.state !== "talking") figure.set("thinking");
      },
      text(snapshot) {
        if (!reply) reply = add({ kind: "blip", text: "" });
        reply.text = snapshot;
        answer = snapshot;
        update(reply);
        figure.set("talking");
      },
      block(block) {
        if (block.type === "text") reply = null;
      },
      message() {},
      tool(call) {
        const description = tools.describe(call);
        figure.set(figureFor(call));
        // Answers he offers show as buttons, not as a step.
        if (call.name === "offer_choices") return;
        progress(`${description}…`);
        presence("working", description);
        if (!steps || chat.log[chat.log.length - 1] !== steps) steps = add({ kind: "steps", steps: [] });
        // Calls that run at once (models) end in any order.
        stepOf.set(call.id, { item: steps, index: steps.steps.length });
        steps.steps.push(description);
        update(steps);
      },
      toolDone(call, result) {
        const text = typeof result.content === "string" ? result.content : result.content[0].text;
        let summary = null;
        if (call.name === "compile") summary = text.startsWith("No errors") ? "no errors" : text.split("\n")[0];
        else if (call.name === "run_program") summary = text.split("\n")[0];
        // A model or picture: where it was saved, or that it was not made.
        else if (MODEL_TOOLS.includes(call.name)) {
          summary = result.is_error ? "not made" : (/saved (?:it|the result) as (\S+?)[ ,(]/.exec(text) || [])[1] || null;
        }
        const step = stepOf.get(call.id);
        if (summary && step) {
          step.item.steps[step.index] += `: ${summary.replace(/[:.]$/, "")}`;
          update(step.item);
        }
        figure.set("thinking");
      },
    };
    let outcome = null, failure = null;
    try {
      // With the World Editor open, the context says what it shows, and his
      // world edits in this request become one undo step in the editor. A
      // TODO note changes nothing, and the context says it is a note.
      const worldLine = resend ? null : await tools.world.contextLine();
      const context = ideContext(ide, worldLine, modelsLine(), todo ? [extra, TODO_CONTEXT].filter(Boolean).join("\n") : extra);
      const content = resend ? [] : [{ type: "text", text: `${context}\n\n${text}` +
        (shown.length ? `\n\n${pictureLines(pictures.slice(0, shown.length), first)}` : "") },
      ...pictures.slice(0, shown.length).map(imageBlock)];
      if (!todo) await tools.world.beginRequest();
      outcome = await converse({ api, chat, level, content, tools, events, signal: controller.signal, save, pack,
        ...(todo ? { maxSteps: TODO_STEPS } : {}) });
      if (outcome.reason === "refusal") {
        add({ kind: "notice", text: "Blip can't help with that one. Try asking for something else." });
      } else if (outcome.reason === "max_tokens") {
        add({ kind: "notice", text: "Blip's answer was too long and got cut off. Ask for a smaller step at a time." });
      } else if (outcome.reason === "steps") {
        add({ kind: "notice", text: "Blip took a lot of steps, so he stopped to check with you. Ask him to carry on if you'd like." });
      } else if (outcome.reason === "stopped") {
        add({ kind: "notice", text: todo && !tools.notes.saved.length ? "Stopped: nothing was saved." : "Stopped." });
      }
    } catch (error) {
      // A TODO note's failure is told after the note is saved as typed.
      if (todo) failure = error;
      else failed(error);
    } finally {
      const changes = tools.finish();
      const main = tools.main && tools.main.before !== tools.main.after ? tools.main : null;
      if (changes.length || main) {
        add({ kind: "changes", changes: changes.map(({ path, before, after }) => ({ path, before, after })), main,
          undone: false });
      }
      const worldStep = await tools.world.finishRequest();
      presence("idle");
      if (worldStep) add({ kind: "world", ...worldStep, undone: false, note: "" });
      // A TODO note Blip didn't write up is saved as typed, unless the user
      // stopped him; its failure then offers no Try again, which would save
      // it twice.
      let asTyped = null;
      const stopped = failure ? failure instanceof BlipError && failure.code === "stopped" : !outcome || outcome.reason === "stopped";
      if (todo && !tools.notes.saved.length && !stopped) {
        try {
          if (await tools.notes.saveAsTyped(todoReason(outcome, failure))) asTyped = todoReason(outcome, failure);
        } catch (error) {
          console.error(error);
        }
      }
      if (failure) failed(failure, { retry: !tools.notes.saved.length });
      const notes = tools.notes.saved;
      if (notes.length) {
        add(todoItem(asTyped));
        ide.status(`Blip saved ${notes.length === 1 ? "a TODO note" : `${notes.length} TODO notes`}: ` +
          `${notes.map((note) => note.path).join(", ")}`);
      }
      // In a buddy session, the next person's turn comes once his step is done.
      if (turn) {
        turn.done(answer + (changes.length ? ` (He changed ${changes.map((change) => change.path).join(", ")}.)` : "") +
          (main ? ` (Run now starts ${main.after}.)` : "") + (worldStep ? " (He changed the world.)" : "") +
          (notes.length ? ` (He saved ${notes.map((note) => note.path).join(", ")}.)` : ""));
      }
      const build = tools.lastBuild;
      if (build && !build.ok && build.errors && build.errors.length) await ide.markErrors(build.errors, false);
      busy = null;
      setBusy(false);
      progress(null);
      await save();
      finished();
      if (hooks.finished) hooks.finished({ reason: outcome ? outcome.reason : "failed", answer, worldStep, changes });
      if (outcome && outcome.reason === "done") {
        const made = tools.models.made;
        figure.set(build && !build.ok ? "surprised" : changes.length || main || worldStep || tools.ran || made || notes.length ?
          "pleased" : "resting");
      }
      await refreshUsage();
      if (chatTokens(chat) > LONG_CHAT && !chat.toldLong) {
        chat.toldLong = true;
        add({ kind: "notice", text: "This chat is getting long, which makes Blip slower and uses more of his energy. " +
          "Start a new chat when you start on something new.", action: "newChat" });
        await save();
      }
    }
  }

  // retry: whether the notice offers Try again.
  function failed(error, { retry = true } = {}) {
    if (!(error instanceof BlipError)) {
      console.error(error);
      add({ kind: "notice", tone: "error", text: `Something went wrong: ${error.message || error}`, action: retry ? "retry" : null });
      figure.set("surprised");
      return;
    }
    // Too large even once made smaller (agent.js): sending it again cannot help.
    if (tooLarge(error)) {
      add({ kind: "notice", tone: "error", text: "This chat has grown too large for Blip to carry on with. " +
        "Start a new chat to go on: your world and files stay as they are.", action: "newChat" });
      figure.set("surprised");
      return;
    }
    switch (error.code) {
      case "stopped":
        add({ kind: "notice", text: "Stopped." });
        break;
      case "signin":
        add({ kind: "notice", text: "Sign in to talk to Blip.", action: "signIn" });
        accountChanged();
        break;
      case "plan":
        add({ kind: "notice", text: `${error.message} Choose a plan in your account.`, action: "account" });
        figure.set("sleepy");
        return;
      case "level":
        add({ kind: "notice", text: error.message, action: "account" });
        return;
      case "allowance":
        if (usage && usage.membership) {
          const day = error.renews ? new Date(error.renews).toLocaleDateString([], { weekday: "short", day: "numeric",
            month: "short" }) : "";
          add({ kind: "notice", text: `${error.message}${day ? ` (He refills ${day}.)` : ""}`,
            action: "topup" });
          figure.set("sleepy");
          return;
        }
        // Falls through: a daily allowance.
      case "global": {
        const time = error.renews ? new Date(error.renews).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
        add({ kind: "notice", text: `${error.message}${time ? ` (His energy fills up again at ${time}.)` : ""}` });
        figure.set("sleepy");
        return;
      }
      case "pack":
        add({ kind: "notice", tone: "error", text: error.message, action: "reload" });
        break;
      case "conversation":
        add({ kind: "notice", tone: "error", text: `${error.message} Start a new chat to go on.`, action: "newChat" });
        break;
      default:
        add({ kind: "notice", tone: "error", text: error.message, action: retry ? "retry" : null });
    }
    figure.set(error.code === "stopped" ? "resting" : "surprised");
  }

  function setBusy(on) {
    send.hidden = on;
    stopButton.hidden = !on;
    panel.classList.toggle("blip-busy", on);
    renderStarters();
  }

  // ------------------------------------------------------------ the panel
  const panelOpen = () => !panel.hidden;
  // The keyboard goes to the text box, or to Sign in when there is none.
  const focusPanel = () => (form.hidden ? signInNote.querySelector("button") : input).focus();
  // Opens his panel, turning him on, with the keyboard unless focus is false.
  function open({ focus = true } = {}) {
    if (!shown) show(true);
    ide.closeMenu();
    if (panelOpen()) {
      if (focus) focusPanel();
      return;
    }
    panel.hidden = false;
    placePanel();
    drawLevels();
    bubble(null);
    working.hidden = !busy;
    accountChanged();
    render();
    if (focus) focusPanel();
    if (!busy && figure.state !== "sleepy") figure.set(focus ? "listening" : "resting");
  }
  // Closes his panel, turning him off.
  function close() {
    if (!panelOpen() || docked) return;
    closeSketch();
    closeViewer();
    showNote(false);
    panel.hidden = true;
    show(false);
    if (!busy && figure.state === "listening") figure.set("resting");
    ide.editor.focus();
  }
  const toggle = () => (panelOpen() ? close() : open());
  // What is sent where shows over the conversation from the i button, until
  // it is pressed again, Esc, or a click elsewhere. Gives whether it was
  // showing.
  function showNote(on) {
    const was = !note.hidden;
    note.hidden = !on;
    infoButton.setAttribute("aria-expanded", String(on));
    return was;
  }
  document.addEventListener("pointerdown", (event) => {
    if (!note.contains(event.target) && !infoButton.contains(event.target)) showNote(false);
  });

  // ------------------------------------------------------------ moving it
  // Online, dragging the head moves the panel, which opens there again, kept
  // in the window; double-clicking the head puts it back. While the World
  // Editor is open, it is at the left of the run pane, over the editor's
  // Scene panel, rather than over its inspector, unless the user has put it
  // somewhere.
  function placePanel() {
    const pane = !place && !docked && ide.worldEditor() && $("run-pane");
    panel.classList.toggle("blip-over-world", !!pane);
    if (pane) panel.style.setProperty("--blip-world-left", `${Math.max(8, Math.round(pane.getBoundingClientRect().left) + 8)}px`);
    panel.classList.toggle("blip-moved", !!place && !docked);
    if (!place || docked || panel.hidden) return;
    place = { left: Math.round(Math.max(0, Math.min(place.left, innerWidth - panel.offsetWidth))),
      top: Math.round(Math.max(0, Math.min(place.top, innerHeight - panel.offsetHeight))) };
    panel.style.setProperty("--blip-left", `${place.left}px`);
    panel.style.setProperty("--blip-top", `${place.top}px`);
  }
  head.addEventListener("pointerdown", (event) => {
    if (docked || event.button !== 0 || event.target.closest("button, .blip-note")) return;
    event.preventDefault();
    const box = panel.getBoundingClientRect();
    const grip = { x: event.clientX - box.left, y: event.clientY - box.top };
    let moved = false;
    head.setPointerCapture(event.pointerId);
    const move = (pointer) => {
      moved = true;
      place = { left: pointer.clientX - grip.x, top: pointer.clientY - grip.y };
      placePanel();
    };
    const end = () => {
      head.removeEventListener("pointermove", move);
      head.removeEventListener("pointerup", end);
      head.removeEventListener("pointercancel", end);
      if (moved) remember.set(PLACE, JSON.stringify(place));
    };
    head.addEventListener("pointermove", move);
    head.addEventListener("pointerup", end);
    head.addEventListener("pointercancel", end);
  });
  head.addEventListener("dblclick", (event) => {
    if (docked || event.target.closest("button, .blip-note")) return;
    place = null;
    remember.set(PLACE, "");
    panel.style.removeProperty("--blip-left");
    panel.style.removeProperty("--blip-top");
    placePanel();
  });
  addEventListener("resize", () => placePanel());

  // The message box's words, with the pictures waiting to go: at once, or
  // as soon as those being prepared are ready.
  function sendMessage() {
    if (busy || (!input.value.trim() && !guides.length && !preparing)) return;
    // A TODO note waits, with its pictures, until there is a folder for it.
    if (isTodo(input.value) && !canSaveTodo()) return renderGuides();
    if (preparing) {
      sendWhenReady = true;
      return noteGuides("Sending as soon as the picture is ready…");
    }
    ask(input.value, { pictures: takeGuides() });
  }

  button.addEventListener("click", () => open());
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    sendMessage();
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      sendMessage();
    }
  });
  // The starters and the Sketch button come and go with the World Editor,
  // which tells the panel nothing: they are checked again as the user comes
  // to the message box.
  input.addEventListener("focus", () => renderStarters());
  form.addEventListener("pointerenter", () => renderStarters());
  input.addEventListener("input", () => {
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
    if (!busy && figure.state !== "sleepy") figure.set("listening");
    if (isTodo(input.value) !== todoShown) renderGuides();
  });
  stopButton.addEventListener("click", () => busy && busy.controller.abort());

  // Talk: click, speak, click again. The words heard so far fill the text box,
  // and the last words are sent as the message.
  let listener = null;
  async function talk() {
    if (!listener) listener = new Listener(cloud);
    if (listener.recording) {
      talkButton.disabled = true;
      try {
        const { text } = await listener.stop();
        input.value = text;
        if (text) ask(text, { pictures: takeGuides() });
        else bubble("I didn't catch that. Try again, a little louder?", [], 6);
      } catch (error) {
        add({ kind: "notice", tone: "error", text: error.message || String(error) });
      } finally {
        talkButton.disabled = false;
        talkButton.setAttribute("aria-pressed", "false");
        talkButton.textContent = "Talk";
        input.placeholder = PLACEHOLDER;
      }
      return;
    }
    if (busy || !canAsk()) return;
    talkButton.disabled = true;
    input.value = "";
    input.placeholder = "Starting the microphone…";
    try {
      // Listening shows once the microphone hears, before the server answers.
      await listener.start((heard) => { input.value = heard; }, () => {
        talkButton.disabled = false;
        talkButton.setAttribute("aria-pressed", "true");
        talkButton.textContent = "Done";
        input.placeholder = "Listening…";
        if (figure.state !== "sleepy") figure.set("listening");
      });
    } catch (error) {
      talkButton.disabled = false;
      talkButton.setAttribute("aria-pressed", "false");
      talkButton.textContent = "Talk";
      input.placeholder = PLACEHOLDER;
      add({ kind: "notice", tone: "error", text: error.message || String(error) });
    }
  }
  talkButton.addEventListener("click", () => talk());
  // Esc closes a picture shown large, or what is sent where, or else the
  // panel. The sketch pad's Esc is its Cancel.
  root.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && panelOpen()) {
      event.preventDefault();
      if (closeViewer()) return input.focus();
      if (showNote(false)) return infoButton.focus();
      close();
    }
  });
  // View > Blip helper and the toolbar's Blip turn him off, or on with his
  // panel open.
  for (const control of [menuItem, toolButton]) {
    if (!control) continue;
    control.hidden = false;
    control.addEventListener("click", () => {
      ide.closeMenu();
      toggle();
    });
  }

  show(shown);
  accountChanged();
  await projectChanged();
  // On, he starts with his panel open, leaving the keyboard where it is
  // online.
  if (shown || docked) open({ focus: docked });
  // Hello, once, on a first visit.
  if (shown && !remember.get(HELLO)) {
    remember.set(HELLO, "1");
    setTimeout(() => bubble("Hi, I'm Blip! I'm here if you'd like help with your program.", [], 14), 1500);
  }

  return {
    toggle,
    open,
    close,
    ask,
    accountChanged,
    projectChanged,
    // The World Editor opened or closed: his panel moves to its place for it.
    worldEditorChanged: () => placePanel(),
    // For the desktop IDE's players (desktop.js): Blip's last change to the
    // world taken back, the user's answer to his question, and Stop.
    async undoLastWorld() {
      const item = chat && [...chat.log].reverse().find((entry) => entry.kind === "world" && !entry.undone);
      if (!item) return { ok: false, error: "Blip hasn't changed the world in this chat." };
      await undoWorld(item);
      return item.undone ? { ok: true } : { ok: false, error: item.note || "It could not be undone." };
    },
    answerRemoval(yes) {
      const item = chat && [...chat.log].reverse().find((entry) => entry.kind === "confirm" && !entry.answer && removalAnswers.has(entry));
      if (item) answerRemoval(item, yes);
      return !!item;
    },
    stop() {
      if (busy) busy.controller.abort();
    },
    canAsk,
    // The audio of the last thing said to him, as sent, as a WAV file's
    // bytes, for hearing what ElevenLabs heard; null before any.
    lastHeard: () => (listener ? listener.lastWav() : null),
    // A compile or runtime error of the user's: while his panel is open, Blip
    // offers to fix it, once, in his bubble.
    offer({ kind, errors = [], message = "", location = null }) {
      if (!panelOpen() || busy || !canAsk()) return;
      const key = kind === "compile" ? errors.map((error) => `${error.file}:${error.row}:${error.message}`).join("|") :
        `${message}|${location ? `${location.file}:${location.line}` : ""}`;
      if (!key || offered.has(key)) return;
      offered.add(key);
      figure.set("surprised");
      bubble("Want me to fix that?", [
        ["Fix it", () => ask(kind === "compile" ? "Please fix the errors in my program." :
          "My program stopped with a runtime error. Please fix it.")],
        ["No thanks", () => {}],
      ], 20);
    },
    get busy() { return !!busy; },
    // A request the World Editor sends, such as a recipe's Make
    // (blip_recipes.bb): asked as soon as he is free, for up to five
    // minutes, as a message the user sees in the conversation. Resolves
    // { ok, error } once it is asked, or cannot be.
    async askWhenFree(text, { label = null } = {}) {
      if (!canAsk()) {
        accountChanged();
        open();
        return { ok: false, error: "Blip needs you to sign in first: sign in, then press Make again." };
      }
      if (!ide.project()) return { ok: false, error: "Blip has no project open." };
      const until = Date.now() + 5 * 60000;
      while (busy && Date.now() < until) await new Promise((resolve) => setTimeout(resolve, 500));
      if (busy) return { ok: false, error: "Blip was busy with another request: press Make again when he has finished." };
      ask(text, { label });
      return { ok: true };
    },
    // His figure, for automated checks: figure.state, and figure.set(state).
    figure,
  };
}
